import { useNavigate } from 'react-router-dom';
import Reveal from '../Components/Reveal';
import '../styles/Services.css';
import offshoreImg from '../Assets/offshore.avif';
import wasteWaterImg from '../Assets/waste water management.jpg';
import powerPlantImg from '../Assets/power plant.jpg';
import processingPlantImg from '../Assets/processing_plant.jpg';
import coolingTowerImg from '../Assets/Cooling Tower 5.jpg';
import arWorkshopImg from '../Assets/AR Workshop 1.jpg';
import digitalTwinGoogleImg from '../Assets/digital twin_google.jpeg';
import aiChapterImg from '../Assets/AI.png';
import hsseImg from '../Assets/hsse.jpg';

const Services = () => {
  const navigate = useNavigate();

  return (
    <main className="dw-srv">

      {/* ── HERO ───────────────────────────────────────────── */}
      <header className="dw-srv__hero-wrap">
        <div className="dw-srv__hero">
          <Reveal animation="fade-up">
            <div className="dw-srv__hero-content">
              <span className="dw-srv__hero-eyebrow">Comprehensive Innovation</span>
              <h1 className="dw-srv__hero-heading">Our Services</h1>
              <p className="dw-srv__hero-subtitle">Bridging Physical &amp; Digital</p>
              <p className="dw-srv__hero-sub">
                We design, build, and deploy intelligent systems that integrate seamlessly into real-world operations — securing and connecting the digital thread.
              </p>
            </div>
          </Reveal>
        </div>
      </header>

      {/* ── CHAPTER 01: 3D Digital Twin & XR ─────────────── */}
      <section className="dw-srv__chapter dw-srv__chapter--light">
        <div className="dw-srv__chapter-inner">

          <Reveal animation="fade-right" className="dw-srv__chapter-text">
            <div className="dw-srv__chapter-tag dw-srv__chapter-tag--blue">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>
              Chapter 01
            </div>
            <h2 className="dw-srv__chapter-title">3D Digital Twin &amp; XR Solutions</h2>
            <h4 className="dw-srv__chapter-subtitle">Visualize. Simulate. Optimize.</h4>
            <p className="dw-srv__chapter-body">
              We develop immersive digital twin environments powered by XR, enabling real-time interaction with physical assets and advanced visualization of complex systems. Our platforms bridge the gap between physical infrastructure and digital intelligence.
            </p>
            <p className="dw-srv__chapter-body">
              From offshore oil platforms to industrial facilities, our digital twins provide operators with a real-time, interactive view of their assets—enabling faster decisions, predictive maintenance, and zero-downtime operations.
            </p>
            <div className="dw-srv__chapter-checklist">
              {[
                { label: 'XR-enabled twins', desc: 'Immersive AR/VR asset interaction' },
                { label: 'Advanced Visualization', desc: 'Real-time 3D data overlays' },
                { label: 'Remote collaboration', desc: 'Multi-user virtual environments' },
                { label: 'Interactive 3D', desc: 'Live simulation & scenario testing' },
              ].map(item => (
                <div key={item.label} className="dw-srv__chapter-check">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  <div>
                    <div className="dw-srv__check-label">{item.label}</div>
                    <div className="dw-srv__check-desc">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal animation="fade-left" delay={120} className="dw-srv__chapter-img-col" style={{ clipPath: 'none', overflow: 'visible' }}>
            <div className="dw-srv__chapter-img-wrap">
              <img
                src={digitalTwinGoogleImg}
                alt="XR Digital Twin"
                className="dw-srv__chapter-img"
              />
            </div>
            <div className="dw-srv__chapter-case dw-srv__chapter-case--dark dw-srv__chapter-case--br">
              <span className="dw-srv__case-eyebrow">Case Study: Offshore O&amp;G</span>
              <p className="dw-srv__case-body">Developed an XR-enabled digital twin platform for remote monitoring and interaction.</p>
              <div className="dw-srv__case-footer">
                <span className="dw-srv__case-footer-label">Reduced Site Visits</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6DC04B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
              </div>
            </div>
          </Reveal>

        </div>
      </section>

      {/* ── CHAPTER 02: AI & ML ───────────────────────────── */}
      <section className="dw-srv__chapter dw-srv__chapter--dark-bg">
        <div className="dw-srv__chapter-inner dw-srv__chapter-inner--reverse">

          <Reveal animation="fade-right" className="dw-srv__chapter-img-col" style={{ clipPath: 'none', overflow: 'visible' }}>
            <div className="dw-srv__chapter-img-wrap dw-srv__chapter-img-wrap--sq">
              <img
                src={aiChapterImg}
                alt="AI Analytics"
                className="dw-srv__chapter-img dw-srv__chapter-img--dim"
              />
            </div>
            <div className="dw-srv__chapter-case dw-srv__chapter-case--blue dw-srv__chapter-case--tl">
              <span className="dw-srv__case-eyebrow dw-srv__case-eyebrow--light">Case Study: Pulse Data</span>
              <p className="dw-srv__case-body dw-srv__case-body--white">Developed centralized analytics system with predictive capabilities.</p>
            </div>
          </Reveal>

          <Reveal animation="fade-left" delay={120} className="dw-srv__chapter-text">
            <div className="dw-srv__chapter-tag dw-srv__chapter-tag--glass">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
              Chapter 02
            </div>
            <h2 className="dw-srv__chapter-title dw-srv__chapter-title--white">AI &amp; Analytics Solutions</h2>
            <h4 className="dw-srv__chapter-subtitle dw-srv__chapter-subtitle--muted">Intelligence That Drives Action</h4>
            <p className="dw-srv__chapter-body dw-srv__chapter-body--light">
              We design AI systems that transform raw data into predictive and automated workflows, driving actionable insights through advanced machine learning and analytics pipelines.
            </p>
            <p className="dw-srv__chapter-body dw-srv__chapter-body--light">
              Our solutions integrate seamlessly with existing infrastructure—whether cloud or on-premise—to deliver real-time intelligence that empowers operations, reduces waste, and accelerates decision-making at scale.
            </p>
            <div className="dw-srv__chapter-features">
              {[
                { icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/></svg>, label: 'Predictive Analytics' },
                { icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>, label: 'Data Management' },
                { icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6" rx="1"/><path d="M15 2v2M15 20v2M2 15h2M2 9h2M20 15h2M20 9h2M9 2v2M9 20v2"/></svg>, label: 'ML Model Dev' },
                { icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>, label: 'Decision Systems' },
              ].map(f => (
                <div key={f.label} className="dw-srv__chapter-feat">
                  <span className="dw-srv__chapter-feat-icon">{f.icon}</span>
                  <span className="dw-srv__chapter-feat-label">{f.label}</span>
                </div>
              ))}
            </div>
          </Reveal>

        </div>
      </section>

      {/* ── CHAPTER 03: IoT & Networking ─────────────────── */}
      <section className="dw-srv__chapter dw-srv__chapter--light">
        <div className="dw-srv__chapter-inner">

          <Reveal animation="fade-right" className="dw-srv__chapter-text">
            <div className="dw-srv__chapter-tag dw-srv__chapter-tag--emerald">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="2"/><path d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49m11.31-2.82a10 10 0 0 1 0 14.14m-14.14 0a10 10 0 0 1 0-14.14"/></svg>
              Chapter 03
            </div>
            <h2 className="dw-srv__chapter-title">IoT &amp; Networking</h2>
            <h4 className="dw-srv__chapter-subtitle">The Foundation of Connectivity</h4>
            <p className="dw-srv__chapter-body">
              Enabling real-time data flow from the edge. We deploy industrial IoT sensors and robust networking infrastructure to ensure your intelligence layer is always powered by live, accurate data.
            </p>
            <p className="dw-srv__chapter-body">
              From sensor installation to full-stack network architecture, we build the backbone that connects your physical assets to your digital systems—securely and reliably at industrial scale.
            </p>
            <div className="dw-srv__chapter-rows">
              <div className="dw-srv__chapter-row">
                <div className="dw-srv__row-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>
                </div>
                <div>
                  <div className="dw-srv__row-title">Industrial Networking</div>
                  <div className="dw-srv__row-desc">Ruggedized network design for OT/IT environments</div>
                </div>
              </div>
              <div className="dw-srv__chapter-row">
                <div className="dw-srv__row-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6" rx="1"/><path d="M15 2v2M15 20v2M2 15h2M2 9h2M20 15h2M20 9h2M9 2v2M9 20v2"/></svg>
                </div>
                <div>
                  <div className="dw-srv__row-title">Smart IoT Deployment</div>
                  <div className="dw-srv__row-desc">End-to-end sensor integration and edge computing</div>
                </div>
              </div>
              <div className="dw-srv__chapter-row">
                <div className="dw-srv__row-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>
                </div>
                <div>
                  <div className="dw-srv__row-title">Cyber Security</div>
                  <div className="dw-srv__row-desc">Protecting industrial endpoints from the ground up</div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal animation="fade-left" delay={120} className="dw-srv__chapter-img-col" style={{ clipPath: 'none', overflow: 'visible' }}>
            <div className="dw-srv__chapter-img-wrap dw-srv__chapter-img-wrap--4-3">
              <img
                src="https://images.unsplash.com/photo-1558346490-a72e53ae2d4f?w=1000&auto=format&fit=crop"
                alt="Networking"
                className="dw-srv__chapter-img"
              />
            </div>
            <div className="dw-srv__chapter-case dw-srv__chapter-case--white dw-srv__chapter-case--tr">
              <div className="dw-srv__case-icon dw-srv__case-icon--red">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>
              </div>
              <h5 className="dw-srv__case-title">Cyber Security</h5>
              <p className="dw-srv__case-body dw-srv__case-body--sm">Protecting industrial endpoints from the ground up.</p>
            </div>
          </Reveal>

        </div>
      </section>

      {/* ── CHAPTER 04: Training & Simulation ────────────── */}
      <section className="dw-srv__chapter dw-srv__chapter--blue-tint">
        <div className="dw-srv__chapter-inner dw-srv__chapter-inner--reverse">

          <Reveal animation="fade-right" className="dw-srv__chapter-img-col" style={{ clipPath: 'none', overflow: 'visible' }}>
            <div className="dw-srv__chapter-img-wrap dw-srv__chapter-img-wrap--bordered">
              <img
                src={hsseImg}
                alt="VR Training"
                className="dw-srv__chapter-img dw-srv__chapter-img--grayscale"
              />
            </div>
            <div className="dw-srv__chapter-case dw-srv__chapter-case--dark dw-srv__chapter-case--tl">
              <span className="dw-srv__case-eyebrow">HSSE Certified</span>
              <p className="dw-srv__case-body">Improving learning retention and safety awareness across hazardous sites.</p>
            </div>
          </Reveal>

          <Reveal animation="fade-left" delay={120} className="dw-srv__chapter-text">
            <div className="dw-srv__chapter-tag dw-srv__chapter-tag--solid-blue">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>
              Chapter 04
            </div>
            <h2 className="dw-srv__chapter-title">Training &amp; Simulation</h2>
            <h4 className="dw-srv__chapter-subtitle">Real-World Readiness</h4>
            <p className="dw-srv__chapter-body">
              We create XR-based training environments that simulate real-world scenarios safely and effectively, reducing risks in high-stakes industries. Our modules are certified and deployed across leading oil, gas, and utilities firms.
            </p>
            <p className="dw-srv__chapter-body">
              Learners step into photorealistic virtual environments—practicing emergency procedures, equipment handling, and HSSE protocols without ever setting foot on a live site. Better retention, fewer incidents, faster onboarding.
            </p>
            <div className="dw-srv__chapter-pills">
              {['VR training modules', 'HSSE safety modules'].map(p => (
                <span key={p} className="dw-srv__chapter-pill">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2AAECC" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                  {p}
                </span>
              ))}
            </div>
          </Reveal>

        </div>
      </section>

      {/* ── MILESTONE PORTFOLIO ───────────────────────────── */}
      <section className="dw-srv__portfolio">
        <div className="dw-srv__portfolio-inner">
          <Reveal>
            <div className="dw-srv__portfolio-header">
              <h2 className="dw-srv__portfolio-heading">Milestone Portfolio</h2>
              <p className="dw-srv__portfolio-sub">Global Project Deployments</p>
              <div className="dw-srv__portfolio-rule" />
            </div>
          </Reveal>

          {/* Staggered 3-col grid — 9 cards */}
          <div className="dw-srv__portfolio-grid">

            {/* Col 1 — no offset */}
            <Reveal className="dw-srv__portfolio-col" style={{ clipPath: 'none', overflow: 'visible' }}>
              <div className="dw-srv__proj-card">
                <div className="dw-srv__proj-img-wrap">
                  <img src={offshoreImg} alt="Offshore Platform" className="dw-srv__proj-img" />
                </div>
                <div className="dw-srv__proj-info">
                  <span className="dw-srv__proj-tag dw-srv__proj-tag--blue">Energy / Oil &amp; Gas</span>
                  <h4 className="dw-srv__proj-title">ARVR &amp; Digital Twin Application For Oil and Gas Offshore Platform</h4>
                </div>
              </div>
              <div className="dw-srv__proj-card">
                <div className="dw-srv__proj-img-wrap">
                  <img src={wasteWaterImg} alt="Waste Water" className="dw-srv__proj-img" />
                </div>
                <div className="dw-srv__proj-info">
                  <span className="dw-srv__proj-tag dw-srv__proj-tag--cyan">Utilities / Environmental</span>
                  <h4 className="dw-srv__proj-title">VR Simulation for Waste Water Management</h4>
                </div>
              </div>
              <div className="dw-srv__proj-card">
                <div className="dw-srv__proj-img-wrap">
                  <img src="https://images.unsplash.com/photo-1537462715879-360eeb61a0ad?w=800&auto=format&fit=crop" alt="HYMAC" className="dw-srv__proj-img" />
                </div>
                <div className="dw-srv__proj-info">
                  <span className="dw-srv__proj-tag dw-srv__proj-tag--blue">Technical Visualization</span>
                  <h4 className="dw-srv__proj-title">HYMAC 3D Animation: Cinematic Hardware Visualization</h4>
                </div>
              </div>
            </Reveal>

            {/* Col 2 — offset1 (mt-16) */}
            <Reveal delay={80} className="dw-srv__portfolio-col dw-srv__portfolio-col--offset1" style={{ clipPath: 'none', overflow: 'visible' }}>
              <div className="dw-srv__proj-card">
                <div className="dw-srv__proj-img-wrap">
                  <img src={coolingTowerImg} alt="Cooling Tower" className="dw-srv__proj-img" />
                </div>
                <div className="dw-srv__proj-info">
                  <span className="dw-srv__proj-tag dw-srv__proj-tag--purple">Infrastructure / XR</span>
                  <h4 className="dw-srv__proj-title">Dynamic 4D Simulation and XR Platform for Cooling Tower Upgrading (CITRUS)</h4>
                </div>
              </div>
              <div className="dw-srv__proj-card">
                <div className="dw-srv__proj-img-wrap">
                  <img src={powerPlantImg} alt="Power Plant" className="dw-srv__proj-img" />
                </div>
                <div className="dw-srv__proj-info">
                  <span className="dw-srv__proj-tag dw-srv__proj-tag--orange">Heavy Engineering</span>
                  <h4 className="dw-srv__proj-title">VR Turbine, Compressor and Heat Exchanger in Power Plant</h4>
                </div>
              </div>
              <div className="dw-srv__proj-card">
                <div className="dw-srv__proj-img-wrap">
                  <img src={arWorkshopImg} alt="MakersLab" className="dw-srv__proj-img" />
                </div>
                <div className="dw-srv__proj-info">
                  <span className="dw-srv__proj-tag dw-srv__proj-tag--purple">Community Hub</span>
                  <h4 className="dw-srv__proj-title">AR Workshop for MakersLab: Spatial Design Hub</h4>
                </div>
              </div>
            </Reveal>

            {/* Col 3 — offset2 (mt-32) */}
            <Reveal delay={160} className="dw-srv__portfolio-col dw-srv__portfolio-col--offset2" style={{ clipPath: 'none', overflow: 'visible' }}>
              <div className="dw-srv__proj-card">
                <div className="dw-srv__proj-img-wrap">
                  <img src="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=800&auto=format&fit=crop" alt="Safety Training" className="dw-srv__proj-img" />
                </div>
                <div className="dw-srv__proj-info">
                  <span className="dw-srv__proj-tag dw-srv__proj-tag--emerald">Safety / Simulation</span>
                  <h4 className="dw-srv__proj-title">Introduction to Oil &amp; Gas (HSSE) VR Simulation Project</h4>
                </div>
              </div>
              <div className="dw-srv__proj-card">
                <div className="dw-srv__proj-img-wrap">
                  <img src={processingPlantImg} alt="Processing Plant" className="dw-srv__proj-img" />
                </div>
                <div className="dw-srv__proj-info">
                  <span className="dw-srv__proj-tag dw-srv__proj-tag--sky">Industrial Digital Twins</span>
                  <h4 className="dw-srv__proj-title">Digital Twin Application for Processing Plant</h4>
                </div>
              </div>
              <div className="dw-srv__proj-card">
                <div className="dw-srv__proj-img-wrap">
                  <img src="https://images.unsplash.com/photo-1587620962725-abab7fe55159?w=800&auto=format&fit=crop" alt="Certified Training" className="dw-srv__proj-img" />
                </div>
                <div className="dw-srv__proj-info">
                  <span className="dw-srv__proj-tag dw-srv__proj-tag--blue">Professional Training</span>
                  <h4 className="dw-srv__proj-title">UE5: AR/VR Development, HDR Corp Certified Training</h4>
                </div>
              </div>
            </Reveal>

          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ──────────────────────────────────── */}
      <section className="dw-srv__testi">
        <div className="dw-srv__testi-inner">

          <Reveal animation="fade-right" className="dw-srv__testi-left" style={{ clipPath: 'none', overflow: 'visible' }}>
            <h2 className="dw-srv__testi-heading">Testimonial</h2>
            <div className="dw-srv__testi-stat-card">
              <p className="dw-srv__testi-stat-val">10+</p>
              <p className="dw-srv__testi-stat-label">Global Clients Served</p>
            </div>
          </Reveal>

          <div className="dw-srv__testi-quotes">
            <Reveal animation="fade-up" style={{ clipPath: 'none', overflow: 'visible' }}>
              <div className="dw-srv__quote-card dw-srv__quote-card--white">
                <svg className="dw-srv__quote-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/></svg>
                <p className="dw-srv__quote-text">"Teczo has made our training sessions more effective and engaging. Student can practice real-world scenarios in a virtual setting, improving their skills and safety awareness."</p>
                <div className="dw-srv__quote-author">
                  <div className="dw-srv__quote-avatar dw-srv__quote-avatar--blue-light">UTP</div>
                  <div>
                    <p className="dw-srv__quote-name">Centre of teaching &amp; learning</p>
                    <p className="dw-srv__quote-org">University Technology Petronas</p>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal animation="fade-up" delay={150} style={{ clipPath: 'none', overflow: 'visible' }}>
              <div className="dw-srv__quote-card dw-srv__quote-card--blue">
                <svg className="dw-srv__quote-icon dw-srv__quote-icon--white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/></svg>
                <p className="dw-srv__quote-text dw-srv__quote-text--white">"Partnering with Teczo has been an incredible experience. Their innovative solutions, from AI Driven analytics to XR applications, have propelled our business into the future delivering unmatched results."</p>
                <div className="dw-srv__quote-author">
                  <div className="dw-srv__quote-avatar dw-srv__quote-avatar--white-glass">AA</div>
                  <div>
                    <p className="dw-srv__quote-name dw-srv__quote-name--white">Andrey Abraham</p>
                    <p className="dw-srv__quote-org dw-srv__quote-org--light">CEO of Pulse Data Technologies</p>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal animation="fade-up" delay={300} style={{ clipPath: 'none', overflow: 'visible' }}>
              <div className="dw-srv__quote-card dw-srv__quote-card--slate">
                <svg className="dw-srv__quote-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/></svg>
                <p className="dw-srv__quote-text">"FusionSite360XR has completely transformed how we manage our project. The ability to visualize and collaborate on complex oil &amp; gas offshore and onshore sites remotely has saved us time and significantly improved efficiency."</p>
                <div className="dw-srv__quote-author">
                  <div className="dw-srv__quote-avatar dw-srv__quote-avatar--dark">NC</div>
                  <div>
                    <p className="dw-srv__quote-name">NervCentre</p>
                    <p className="dw-srv__quote-org">PETRONAS</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

        </div>
      </section>

      {/* ── FOOTER CTA ────────────────────────────────────── */}
      <footer className="dw-srv__footer-wrap">
        <div className="dw-srv__footer-cta">
          <Reveal animation="fade-up">
            <div className="dw-srv__footer-inner">
              <h2 className="dw-srv__footer-heading">Transform How You Build,<br />Train, and Operate</h2>
              <p className="dw-srv__footer-sub">
                DigitalWorks delivers immersive, intelligent systems that redefine efficiency, collaboration, and innovation.
              </p>
              <button className="dw-srv__footer-btn-white" onClick={() => navigate('/contact')}>Start Your Project</button>
            </div>
          </Reveal>
        </div>
      </footer>

    </main>
  );
};

export default Services;
